import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { CloserToLeapClientSyncTask } from '@state/closer-tasks/closerTasks.model';
import { CloserTasksState } from '@state/closer-tasks/closerTasks.state';
import { BehaviorSubject, firstValueFrom, Observable, timer } from 'rxjs';
import { delayWhen, retry } from 'rxjs/operators';
import { LeapApiService } from 'src/app/public/leap/leap-api.service';
import { Logger } from '@core/logger';
import { NavigationService } from '@services/nav/nav.service';
import { CALL_TO_ACTION_TYPES } from 'src/app/ui/note/note.component';

enum ClientMergeStatus {
	INDETERMINATE,
	PARTIALLY_MERGED,
	FULLY_MERGED,
	MERGING_IN_PROGRESS,
}

interface MergedClientsCount {
	mergedClients: number | null;
	unmergedClients: number | null;
	totalClients: number | null;
	clientsAddedSinceLastExport: number | null; // New clients that were added in Closer since the last export and that can be exported to LEAP
	previousActualCount: number | null;
	previousExpectedCount: number | null;
	clientsWithConflicts: number | null;
}

interface MergeInProgressClientsCount {
	expectedCount: number; // Expected number of clients to be merged in the current export run while the export is in progress
	actualCount: number; // Actual number of clients that have been merged in the current export run while the export is in progress
}

const DefaultMergedClientsCount: MergedClientsCount = {
	mergedClients: null,
	unmergedClients: null,
	totalClients: null,
	clientsAddedSinceLastExport: null,
	previousActualCount: null,
	previousExpectedCount: null,
	clientsWithConflicts: null,
};

const DefaultMergeInProgressClientsCount: MergeInProgressClientsCount = {
	expectedCount: 0,
	actualCount: 0,
};

@UntilDestroy()
@Component({
	selector: 'app-leap',
	templateUrl: './leap.component.html',
	styleUrls: ['./leap.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class LeapComponent implements OnInit {
	@Select(CloserTasksState.closerToLeapClientSyncTask)
	closerToLeapClientSyncTask$: Observable<CloserToLeapClientSyncTask>;

	mergedClientsCount$ = new BehaviorSubject<MergedClientsCount>(
		structuredClone(DefaultMergedClientsCount),
	);

	ClientMergeStatus = ClientMergeStatus;
	clientMergeStatus$ = new BehaviorSubject<ClientMergeStatus>(
		ClientMergeStatus.INDETERMINATE,
	);

	mergeInProgressClientsCount$ =
		new BehaviorSubject<MergeInProgressClientsCount>(
			structuredClone(DefaultMergeInProgressClientsCount),
		);

	showMergeError$ = new BehaviorSubject(false);
	fetchingClientExportStatus$ = new BehaviorSubject(false);

	CALL_TO_ACTION_TYPES = CALL_TO_ACTION_TYPES;

	constructor(
		private leapApiService: LeapApiService,
		private navService: NavigationService,
	) {}

	async ngOnInit(): Promise<void> {
		this.closerToLeapClientSyncTask$
			.pipe(untilDestroyed(this))
			.subscribe((task) => {
				Logger.info(`New closerToLeapClientSyncTask value received`);

				if (
					!task?.results ||
					typeof task.results.actualCount !== 'number' ||
					typeof task.results.expectedCount !== 'number'
				) {
					Logger.warn(`Invalid value received for closerToLeapClientSyncTask`);

					this.mergeInProgressClientsCount$.next(
						structuredClone(DefaultMergeInProgressClientsCount),
					);
					return;
				}

				this.mergeInProgressClientsCount$.next({
					expectedCount: task.results.expectedCount,
					actualCount: task.results.actualCount,
				});

				Logger.info(
					`Updated mergedClientsCount: ${JSON.stringify(this.mergeInProgressClientsCount$.value)}`,
				);
			});

		await this.getCurrClientExportsStatus();

		const currMergedClientsCount = this.mergedClientsCount$.value;

		// Restore previous state if clients have been exported before indicated
		// when previousExpectedCount and previousActualCount are available
		if (
			typeof currMergedClientsCount.previousExpectedCount === 'number' &&
			typeof currMergedClientsCount.previousActualCount === 'number'
		) {
			this.updateClientMergeStatus();
		}
	}

	async exportClientsToLeap() {
		let mergeStatusAtStart: MergedClientsCount;

		try {
			Logger.info('Starting client export to LEAP!');

			this.clientMergeStatus$.next(ClientMergeStatus.MERGING_IN_PROGRESS);
			this.showMergeError$.next(false);

			await this.getCurrClientExportsStatus();
			mergeStatusAtStart = structuredClone(this.mergedClientsCount$.value);

			await firstValueFrom(this.leapApiService.exportClients());

			Logger.info('Client export to LEAP completed');
		} catch (error) {
			Logger.error(`Error exporting clients to LEAP: ${JSON.stringify(error)}`);
			this.showMergeError$.next(true);
		} finally {
			this.clientMergeStatus$.next(ClientMergeStatus.INDETERMINATE);
		}

		try {
			await this.getCurrClientExportsStatus(300); // update counts after merge, delay ensures firestore records are updated

			const currMergeStatus = this.mergedClientsCount$.value;

			const mergeErrorOccurred = this.mergeErrorOccurred(
				mergeStatusAtStart,
				currMergeStatus,
			);

			if (mergeErrorOccurred) {
				this.showMergeError$.next(true);
			}

			this.updateClientMergeStatus();
		} catch (err) {
			Logger.error(
				`Error updating client merge status after merge operation: ${JSON.stringify(err)}`,
			);
		}
	}

	async getCurrClientExportsStatus(delayMs = 0) {
		try {
			Logger.info('Fetching current client export status');

			this.fetchingClientExportStatus$.next(true);

			const currExportStatus = await firstValueFrom(
				this.leapApiService.getClientExportsStatus().pipe(
					untilDestroyed(this),
					delayWhen(() => timer(delayMs)),
					retry(3),
				),
			);
			console.log('Client export/merge status', currExportStatus);

			const {
				totalExportedClientsCount,
				totalUnexportedClientsCount,
				totalClientsCount,
				clientsAddedSinceLastExportCount,
				previousActualCount,
				previousExpectedCount,
				clientsWithConflictsCount,
			} = currExportStatus;

			const updatedMergedClientsCount: MergedClientsCount = {
				mergedClients: totalExportedClientsCount,
				unmergedClients: totalUnexportedClientsCount,
				totalClients: totalClientsCount,
				clientsAddedSinceLastExport: clientsAddedSinceLastExportCount,
				previousExpectedCount,
				previousActualCount,
				clientsWithConflicts: clientsWithConflictsCount,
			};

			this.mergedClientsCount$.next(updatedMergedClientsCount);
		} catch (err) {
			Logger.error(
				`Error getting current client export status: ${JSON.stringify(err)}`,
			);
		} finally {
			this.fetchingClientExportStatus$.next(false);
		}
	}

	updateClientMergeStatus() {
		const { mergedClients, totalClients, unmergedClients } =
			this.mergedClientsCount$.value;

		if (
			typeof mergedClients === 'number' &&
			typeof totalClients === 'number' &&
			mergedClients === totalClients
		) {
			this.clientMergeStatus$.next(ClientMergeStatus.FULLY_MERGED);
			return;
		}

		if (typeof unmergedClients === 'number' && unmergedClients > 0) {
			this.clientMergeStatus$.next(ClientMergeStatus.PARTIALLY_MERGED);
			return;
		}

		this.clientMergeStatus$.next(ClientMergeStatus.INDETERMINATE);
	}

	goToClientsMergeReportPage() {
		this.navService.goToProfile_Integrations_Leap_ClientsMergeReport();
	}

	goToFixClientsListPage() {
		this.navService.goToProfile_Integrations_Leap_FixClientsList();
	}

	openIntercomPopup() {
		window.Intercom('show');
	}

	hideMergeError() {
		this.showMergeError$.next(false);
	}

	mergeErrorOccurred(
		prevMergeCounts: MergedClientsCount,
		currMergeCounts: MergedClientsCount,
	): boolean {
		if (prevMergeCounts.totalClients !== currMergeCounts.totalClients) {
			// If the total clients count has changed, we can't determine if there was an error
			return false;
		}

		if (currMergeCounts.totalClients === currMergeCounts.mergedClients) {
			return false; // All clients have been merged
		}

		if (
			currMergeCounts.mergedClients === prevMergeCounts.mergedClients &&
			currMergeCounts.unmergedClients === prevMergeCounts.unmergedClients
		) {
			return false; // No change in merged or unmerged clients
		}

		const totalExportableClients = prevMergeCounts.unmergedClients;
		const clientsSuccessfullyExported =
			currMergeCounts.mergedClients - prevMergeCounts.mergedClients;
		const clientsWithConflicts = currMergeCounts.clientsWithConflicts;

		if (
			clientsSuccessfullyExported + clientsWithConflicts !==
			totalExportableClients
		) {
			// If the number of clients successfully exported and the number of clients with conflicts
			// don't add up to the number of total exportable clients, infer that an error occurred
			return true;
		}

		return false;
	}
}
